import { useEffect, useMemo, useState } from "react";
import { FileDown, Award, Loader2, Mail, Phone, MapPin, ScrollText, Send, X, Download, RefreshCw, Search, IdCard } from "lucide-react";
import { toast } from "sonner";
import { GermanDatePicker } from "@/components/ui/date-picker";
import PlatzreifeDetail, { fmtDE, durationLabel, statusStyle, restLabel } from "./PlatzreifeDetail";

const STATUS_FILTERS = [
  { key: "", label: "Alle" },
  { key: "active", label: "Aktiv" },
  { key: "completed", label: "Platzreife abgeschlossen" },
];

const PR_FILTERS = [
  { key: "pr_status", value: "active", label: "Aktiv (Laufzeit)" },
  { key: "pr_status", value: "not_started", label: "Noch nicht gestartet" },
  { key: "pr_status", value: "expired", label: "Abgelaufen" },
  { key: "pr_status", value: "completed", label: "Abgeschlossen" },
  { key: "duration", value: "1", label: "1 Monat" },
  { key: "duration", value: "3", label: "3 Monate" },
];

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return "—"; }
};
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return "—"; }
};
const errMsg = (e) => e?.response?.data?.detail || "Aktion fehlgeschlagen";

const CertificateModal = ({ client, base, student, onClose, onSent }) => {
  const [name, setName] = useState(`${student.first_name} ${student.last_name}`.trim());
  const [date, setDate] = useState((student.completed_at || student.period_end || student.period_start || new Date().toISOString()).slice(0, 10));
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);

  const build = async () => {
    setBusy(true);
    try {
      const res = await client.post(`${base}/platzreife/certificate?format=png`, { email: student.email, name, date }, { responseType: "blob" });
      const u = URL.createObjectURL(res.data);
      setUrl((old) => { if (old) URL.revokeObjectURL(old); return u; });
    } catch (e) {
      const detail = e?.response?.data instanceof Blob ? "Urkunde konnte nicht erzeugt werden" : errMsg(e);
      toast.error(detail);
    } finally { setBusy(false); }
  };
  useEffect(() => { build(); /* eslint-disable-next-line */ }, []);

  const download = async () => {
    try {
      const res = await client.post(`${base}/platzreife/certificate`, { email: student.email, name, date }, { responseType: "blob" });
      const u = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = u;
      a.download = `DGV-Platzreife-${name.replace(/[^A-Za-z0-9]+/g, "-")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(u), 4000);
    } catch { toast.error("Download fehlgeschlagen"); }
  };

  const send = async () => {
    if (!window.confirm(`Urkunde jetzt per E-Mail an ${student.email} senden?`)) return;
    setSending(true);
    try {
      const { data } = await client.post(`${base}/platzreife/certificate/send`, { email: student.email, name, date });
      if (data.ok) { toast.success(`Urkunde an ${data.to} gesendet`); onSent?.(data.certificate); onClose(); }
      else toast.error(`Versand fehlgeschlagen (${data.delivery})`);
    } catch (e) { toast.error(errMsg(e)); } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center p-3 sm:p-6" data-testid="certificate-modal">
      <div className="absolute inset-0 bg-black/85" onClick={onClose} />
      <div className="relative bg-[#0A0A0A] border border-[#ff0033]/40 w-full max-w-4xl max-h-[92vh] flex flex-col">
        <div className="flex items-start justify-between gap-4 p-4 border-b border-white/10">
          <div>
            <p className="font-mono-lab text-[10px] tracking-widest text-[#ff0033] uppercase">// DGV-Platzreife Urkunde</p>
            <h3 className="font-heading text-xl uppercase text-white mt-1">{student.first_name} {student.last_name}</h3>
            <p className="text-neutral-500 text-xs mt-1">Wird nur auf Knopfdruck versendet – prüfe zuerst das Layout.</p>
          </div>
          <button onClick={onClose} data-testid="certificate-close" className="text-neutral-400 hover:text-white"><X size={18} /></button>
        </div>

        <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3 border-b border-white/10">
          <label className="block md:col-span-1">
            <span className="font-mono-lab text-[10px] tracking-widest text-neutral-400 uppercase">Name auf der Urkunde</span>
            <input value={name} onChange={(e) => setName(e.target.value)} data-testid="certificate-name"
              className="w-full bg-[#141414] border border-white/10 text-white text-sm p-2.5 outline-none focus:border-[#ff0033]" />
          </label>
          <label className="block md:col-span-1">
            <span className="font-mono-lab text-[10px] tracking-widest text-neutral-400 uppercase">Datum</span>
            <GermanDatePicker value={date} onChange={setDate} placeholder="Datum" className="p-2.5" testId="certificate-date" />
          </label>
          <div className="flex items-end gap-2">
            <button onClick={build} disabled={busy} data-testid="certificate-rebuild"
              className="flex-1 flex items-center justify-center gap-2 border border-white/15 text-neutral-200 hover:border-[#ff0033] font-mono-lab text-[11px] uppercase tracking-widest px-3 py-2.5 disabled:opacity-50">
              {busy ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Vorschau
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto bg-neutral-900 min-h-[320px] flex items-start justify-center p-3">
          {url ? (
            <img src={url} alt="Urkunden-Vorschau" data-testid="certificate-preview"
              className="max-h-[62vh] w-auto shadow-2xl border border-white/10" />
          ) : (
            <div className="h-[320px] flex items-center justify-center text-neutral-500 gap-2 font-mono-lab text-sm">
              {busy ? <><Loader2 size={16} className="animate-spin" /> Urkunde wird erstellt…</> : "Keine Vorschau"}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 p-4 border-t border-white/10">
          {student.certificate?.sent_at && (
            <span className="text-neutral-500 text-xs mr-auto">Bereits gesendet am {fmtDateTime(student.certificate.sent_at)}</span>
          )}
          <button onClick={download} disabled={!url} data-testid="certificate-download"
            className="flex items-center gap-2 border border-white/15 text-neutral-200 hover:border-[#ff0033] font-mono-lab text-[11px] uppercase tracking-widest px-4 py-2.5 disabled:opacity-50">
            <Download size={14} /> PDF herunterladen
          </button>
          <button onClick={send} disabled={sending || !url} data-testid="certificate-send"
            className="flex items-center gap-2 bg-[#ff0033] text-white font-heading uppercase tracking-wider px-6 py-2.5 hover:bg-[#cc0029] disabled:opacity-50">
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Per E-Mail senden
          </button>
        </div>
      </div>
    </div>
  );
};

export const PlatzreifeBoard = ({ client, base = "/pro", kicker = "// ZENTRALE PLATZREIFE-AKTE", canEdit = true, canEditDetails = false }) => {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("");
  const [course, setCourse] = useState("");
  const [prStatus, setPrStatus] = useState("");
  const [duration, setDuration] = useState("");
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [busyKey, setBusyKey] = useState("");
  const [certFor, setCertFor] = useState(null);
  const [detailFor, setDetailFor] = useState(null);

  useEffect(() => { const t = setTimeout(() => setSearch(query.trim()), 400); return () => clearTimeout(t); }, [query]);

  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (status) p.set("status", status);
    if (course) p.set("course", course);
    if (prStatus) p.set("pr_status", prStatus);
    if (duration) p.set("duration", duration);
    if (search) p.set("q", search);
    return p.toString();
  }, [status, course, prStatus, duration, search]);

  const load = () => {
    client.get(`${base}/platzreife${params ? `?${params}` : ""}`).then((r) => setData(r.data)).catch(() => toast.error("Laden fehlgeschlagen"));
  };
  useEffect(load, [params]);

  const checks = data?.checks || [];
  const students = data?.students || [];

  const statsOf = (nc) => {
    let keys = checks.map((c) => c.key);
    if (!nc.bag_geliehen) keys = keys.filter((k) => k !== "bag_geliehen" && k !== "bag_zurueck");
    const done = keys.filter((k) => nc[k]).length;
    const total = keys.length;
    const percent = total ? Math.round((done / total) * 100) : 0;
    return { done, total, percent, completed: done >= total };
  };

  const toggle = async (s, key) => {
    if (!canEdit) return;
    const value = !s.checks[key];
    setBusyKey(`${s.email}:${key}`);
    setData((d) => ({ ...d, students: d.students.map((x) => x.email !== s.email ? x : (() => {
      const nc = { ...x.checks, [key]: value };
      return { ...x, checks: nc, ...statsOf(nc) };
    })()) }));
    try {
      const { data: res } = await client.post(`${base}/platzreife/check`, { email: s.email, first_name: s.first_name, last_name: s.last_name, key, value });
      setData((d) => ({ ...d, students: d.students.map((x) => x.email !== s.email ? x : { ...x, completed_at: res.completed_at }) }));
      if (res.completed) toast.success(`Platzreife komplett – Urkunde für ${s.first_name} ${s.last_name} kann erstellt werden`);
    } catch (e) {
      toast.error(errMsg(e));
      load();
    } finally { setBusyKey(""); }
  };

  const markSent = (email, cert) => setData((d) => ({ ...d, students: d.students.map((x) => x.email === email ? { ...x, certificate: cert } : x) }));

  const downloadList = async () => {
    try {
      const res = await client.get(`${base}/platzreife.pdf${params ? `?${params}` : ""}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Platzreife_Uebersicht_${new Date().toLocaleDateString("de-DE").replaceAll(".", "-")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch { toast.error("PDF-Download fehlgeschlagen"); }
  };

  const CertButton = ({ s, mobile }) => (
    <button onClick={() => setCertFor(s)} data-testid={`certificate-btn-${s.email}`}
      className={`inline-flex items-center gap-1.5 border border-green-500/50 text-green-400 hover:bg-green-500/10 font-mono-lab uppercase tracking-widest ${mobile ? "text-[10px] px-3 py-2 w-full justify-center" : "text-[9px] px-2 py-1.5 mt-1.5"}`}>
      <ScrollText size={mobile ? 13 : 11} /> {s.certificate?.sent_at ? "Urkunde erneut" : "Urkunde erstellen"}
    </button>
  );

  return (
    <div data-testid="platzreife-board">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="font-mono-lab text-[10px] tracking-[0.25em] text-[#ff0033] uppercase">{kicker}</p>
          <h1 className="font-heading text-3xl md:text-4xl uppercase tracking-tight mt-1 text-white">Platzreife</h1>
          <p className="text-neutral-400 text-sm mt-2 max-w-2xl">Alle Schüler aus sämtlichen Platzreife-Kursen zentral verwalten: Ausbildungsbereiche abhaken, Fortschritt verfolgen, Liste exportieren und nach Abschluss die DGV-Platzreife-Urkunde erstellen. Pro-Portal und Admin-Backend arbeiten auf derselben Datenbasis.</p>
        </div>
        <button onClick={downloadList} data-testid="platzreife-pdf-btn"
          className="bg-[#ff0033] text-white font-heading uppercase tracking-wider px-5 py-3 text-sm hover:bg-[#cc0029] transition-colors inline-flex items-center gap-2">
          <FileDown size={16} /> Platzreife-Liste als PDF herunterladen
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-6">
        {STATUS_FILTERS.map((f) => (
          <button key={f.key} onClick={() => setStatus(f.key)} data-testid={`platzreife-filter-${f.key || "all"}`}
            className={`font-mono-lab text-[11px] uppercase tracking-widest px-4 py-2 border transition-colors ${status === f.key ? "border-[#ff0033] text-white bg-[#ff0033]/10" : "border-white/15 text-neutral-400 hover:text-white"}`}>
            {f.label}
          </button>
        ))}
        <select value={course} onChange={(e) => setCourse(e.target.value)} data-testid="platzreife-filter-course"
          className="bg-[#141414] border border-white/15 text-white text-sm px-3 py-2 outline-none focus:border-[#ff0033]">
          <option value="">Alle Platzreife-Kurse</option>
          {(data?.courses || []).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <span className="text-neutral-500 text-xs ml-auto" data-testid="platzreife-count">{data ? `${students.length} Schüler` : "lädt…"}</span>
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-3">
        {PR_FILTERS.map((f) => {
          const active = f.key === "pr_status" ? prStatus === f.value : duration === f.value;
          const toggle = () => {
            if (f.key === "pr_status") { setPrStatus(active ? "" : f.value); }
            else { setDuration(active ? "" : f.value); }
          };
          return (
            <button key={`${f.key}-${f.value}`} onClick={toggle} data-testid={`platzreife-prfilter-${f.value}`}
              className={`font-mono-lab text-[11px] uppercase tracking-widest px-4 py-2 border transition-colors ${active ? "border-[#ff0033] text-white bg-[#ff0033]/10" : "border-white/15 text-neutral-400 hover:text-white"}`}>
              {f.label}
            </button>
          );
        })}
        <div className="flex items-center gap-2 bg-[#141414] border border-white/15 px-3 py-2 ml-auto min-w-[240px] focus-within:border-[#ff0033]">
          <Search size={15} className="text-neutral-500 shrink-0" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="platzreife-search"
            placeholder="Name, E-Mail oder Telefon suchen …" aria-label="Schüler suchen"
            className="bg-transparent outline-none text-white text-sm w-full placeholder:text-neutral-600" />
        </div>
      </div>

      {!data ? (
        <div className="flex items-center gap-2 text-neutral-400 mt-10"><Loader2 size={16} className="animate-spin" /> Laden…</div>
      ) : students.length === 0 ? (
        <p className="text-neutral-400 text-sm mt-10">Keine Platzreife-Schüler für den gewählten Filter.</p>
      ) : (
        <>
        <div className="mt-5 overflow-x-auto border border-white/10 hidden md:block" data-testid="platzreife-table-wrap">
          <table className="w-full text-sm min-w-[1880px]">
            <thead>
              <tr className="bg-[#111] text-neutral-400 font-mono-lab text-[9px] uppercase tracking-wider text-left">
                <th className="p-2.5 sticky left-0 bg-[#111] z-10">Schüler</th>
                <th className="p-2.5">Geburtsdatum</th>
                <th className="p-2.5 min-w-[170px]">Kontakt</th>
                <th className="p-2.5 min-w-[130px]">Adresse</th>
                <th className="p-2.5 min-w-[120px]">Platzreife-Modell</th>
                <th className="p-2.5">PR-Start</th>
                <th className="p-2.5">PR-Laufzeit</th>
                <th className="p-2.5">PR-Ende</th>
                <th className="p-2.5">Restlaufzeit</th>
                <th className="p-2.5">Status</th>
                {checks.map((c) => <th key={c.key} className="p-1.5 text-center whitespace-nowrap">{c.label}</th>)}
                <th className="p-2.5 min-w-[170px]">Fortschritt & Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {students.map((s) => (
                <tr key={s.email} className={`border-t border-white/5 ${s.completed ? "bg-green-500/5" : ""}`} data-testid={`platzreife-row-${s.email}`}>
                  <td className="p-2.5 sticky left-0 bg-[#0B0B0B] z-10">
                    <button type="button" onClick={() => setDetailFor(s)} data-testid={`platzreife-open-${s.email}`}
                      className="text-white font-medium text-left hover:text-[#ff0033] transition-colors">{s.first_name} {s.last_name}</button>
                    {(s.persons || 1) > 1 && <p className="text-[#ff0033] font-mono-lab text-[10px] uppercase tracking-widest mt-0.5">Teilnehmeranzahl: {s.persons}</p>}
                  </td>
                  <td className="p-2.5 text-xs whitespace-nowrap" data-testid={`platzreife-birth-${s.email}`}>
                    {s.birth_date ? <span className="text-neutral-200">{fmtDE(s.birth_date)}{s.age !== null && s.age !== undefined ? <span className="text-neutral-500"> · {s.age} J.</span> : null}</span>
                      : <span className="text-neutral-600">Nicht hinterlegt</span>}
                  </td>
                  <td className="p-2.5 text-xs">
                    <a href={`mailto:${s.email}`} className="text-neutral-300 hover:text-[#ff0033] break-all block">{s.email}</a>
                    {s.phone ? <a href={`tel:${s.phone}`} className="text-neutral-400 hover:text-[#ff0033] block mt-0.5">{s.phone}</a> : <span className="text-neutral-600 block mt-0.5">—</span>}
                  </td>
                  <td className="p-2.5 text-xs text-neutral-300" data-testid={`platzreife-address-${s.email}`}>
                    {s.street || s.postcode || s.city ? (
                      <>
                        <span className="block">{s.street || "—"}</span>
                        <span className="block text-neutral-400">{[s.postcode, s.city].filter(Boolean).join(" ") || "—"}</span>
                      </>
                    ) : <span className="text-neutral-600">keine Adresse hinterlegt</span>}
                  </td>
                  <td className="p-2.5 text-neutral-300 text-xs">{s.pr_model || s.course_name}</td>
                  <td className="p-2.5 text-neutral-300 text-xs whitespace-nowrap" data-testid={`platzreife-start-${s.email}`}>{fmtDE(s.pr_start) || "—"}</td>
                  <td className="p-2.5 text-xs whitespace-nowrap" data-testid={`platzreife-duration-${s.email}`}>
                    <span className={s.pr_duration_months ? "text-white font-medium" : "text-neutral-600"}>{durationLabel(s.pr_duration_months)}</span>
                  </td>
                  <td className="p-2.5 text-neutral-300 text-xs whitespace-nowrap" data-testid={`platzreife-end-${s.email}`}>{fmtDE(s.pr_end) || "—"}</td>
                  <td className="p-2.5 text-neutral-400 text-xs whitespace-nowrap" data-testid={`platzreife-rest-${s.email}`}>{restLabel(s)}</td>
                  <td className="p-2.5">
                    <span className={`font-mono-lab text-[9px] uppercase tracking-widest px-2 py-1 border whitespace-nowrap ${statusStyle(s.pr_status)}`} data-testid={`platzreife-status-${s.email}`}>{s.pr_status_label}</span>
                  </td>
                  {checks.map((c) => {
                    const entfaellt = c.key === "bag_zurueck" && !s.checks.bag_geliehen;
                    return (
                      <td key={c.key} className="p-1.5 text-center">
                        <input type="checkbox" checked={!!s.checks[c.key]}
                          onChange={() => toggle(s, c.key)}
                          disabled={!canEdit || entfaellt || busyKey === `${s.email}:${c.key}`}
                          title={entfaellt ? "Entfällt – eigenes Bag (kein Bag geliehen)" : c.label}
                          data-testid={`platzreife-check-${s.email}-${c.key}`}
                          className={`w-4 h-4 accent-[#ff0033] align-middle ${entfaellt ? "opacity-25 cursor-not-allowed" : "cursor-pointer"}`} />
                      </td>
                    );
                  })}
                  <td className="p-2.5">
                    <p className="text-xs text-neutral-300 whitespace-nowrap">Fortschritt: {s.done} / {s.total ?? checks.length} – {s.percent} %</p>
                    <div className="h-1.5 bg-white/10 mt-1.5 w-full">
                      <div className={`h-full transition-all ${s.completed ? "bg-green-500" : "bg-[#ff0033]"}`} style={{ width: `${s.percent}%` }} />
                    </div>
                    <button type="button" onClick={() => setDetailFor(s)} data-testid={`platzreife-detail-btn-${s.email}`}
                      className="inline-flex items-center gap-1.5 border border-white/15 text-neutral-300 hover:border-[#ff0033] hover:text-white font-mono-lab uppercase tracking-widest text-[9px] px-2 py-1.5 mt-1.5">
                      <IdCard size={11} /> Details
                    </button>
                    {s.completed && (
                      <>
                        <span className="inline-flex items-center gap-1 mt-1.5 text-green-400 font-mono-lab text-[10px] uppercase tracking-widest" data-testid={`platzreife-done-${s.email}`}>
                          <Award size={12} /> Abgeschlossen{s.completed_at ? ` · ${fmtDate(s.completed_at)}` : ""}
                        </span>
                        <br />
                        <CertButton s={s} />
                        {s.certificate?.sent_at && <p className="text-neutral-500 text-[10px] mt-1">Urkunde gesendet am {fmtDateTime(s.certificate.sent_at)}</p>}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Handy & Tablet: Karten-Ansicht */}
        <div className="mt-5 space-y-3 md:hidden" data-testid="platzreife-cards">
          {students.map((s) => (
            <div key={s.email} className={`border p-4 ${s.completed ? "border-green-500/40 bg-green-500/5" : "border-white/10 bg-[#0F0F0F]"}`} data-testid={`platzreife-card-${s.email}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <button type="button" onClick={() => setDetailFor(s)} data-testid={`platzreife-mopen-${s.email}`}
                    className="font-heading text-lg text-white leading-tight text-left">{s.first_name} {s.last_name}</button>
                  <p className="text-neutral-500 text-xs mt-0.5">{s.pr_model || s.course_name}</p>
                </div>
                <span className={`font-mono-lab text-[10px] uppercase tracking-widest px-2 py-0.5 border ${s.completed ? "text-green-400 border-green-500/40" : "text-[#ff0033] border-[#ff0033]/40"}`}>{s.percent} %</span>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className={`font-mono-lab text-[9px] uppercase tracking-widest px-2 py-1 border ${statusStyle(s.pr_status)}`} data-testid={`platzreife-mstatus-${s.email}`}>{s.pr_status_label}</span>
                <span className="text-neutral-400 text-xs">{durationLabel(s.pr_duration_months)} · {restLabel(s)}</span>
              </div>
              <div className="mt-3 flex flex-col gap-1.5 text-sm">
                <span className="flex items-center gap-2 text-neutral-300" data-testid={`platzreife-mbirth-${s.email}`}>
                  <IdCard size={14} className="text-[#ff0033] shrink-0" /> {s.birth_date ? `${fmtDE(s.birth_date)}${s.age != null ? ` · ${s.age} J.` : ""}` : "Geburtsdatum: nicht hinterlegt"}
                </span>
                <a href={`mailto:${s.email}`} className="flex items-center gap-2 text-neutral-300 break-all"><Mail size={14} className="text-[#ff0033] shrink-0" /> {s.email}</a>
                {s.phone && <a href={`tel:${s.phone}`} className="flex items-center gap-2 text-neutral-300"><Phone size={14} className="text-[#ff0033] shrink-0" /> {s.phone}</a>}
                <span className="flex items-start gap-2 text-neutral-300"><MapPin size={14} className="text-[#ff0033] shrink-0 mt-0.5" />
                  <span>{s.street || "—"}<br />{[s.postcode, s.city].filter(Boolean).join(" ") || "—"}</span>
                </span>
                <span className="text-neutral-500 text-xs">Kurszeitraum: {fmtDate(s.period_start)}{s.period_end ? ` – ${fmtDate(s.period_end)}` : ""}</span>
                <span className="text-neutral-500 text-xs" data-testid={`platzreife-mruntime-${s.email}`}>PR-Start: {fmtDE(s.pr_start) || "—"} · PR-Ende: {fmtDE(s.pr_end) || "—"}</span>
                {(s.persons || 1) > 1 && <span className="text-[#ff0033] font-mono-lab text-[10px] uppercase tracking-widest">Teilnehmeranzahl: {s.persons}</span>}
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2">
                {checks.map((c) => {
                  const entfaellt = c.key === "bag_zurueck" && !s.checks.bag_geliehen;
                  return (
                    <label key={c.key} className={`flex items-center gap-2 text-xs ${entfaellt ? "opacity-40" : "text-neutral-300"}`}>
                      <input type="checkbox" checked={!!s.checks[c.key]} onChange={() => toggle(s, c.key)}
                        disabled={!canEdit || entfaellt || busyKey === `${s.email}:${c.key}`}
                        data-testid={`platzreife-mcheck-${s.email}-${c.key}`}
                        className="w-4 h-4 accent-[#ff0033]" />
                      {c.label}
                    </label>
                  );
                })}
              </div>
              <div className="h-1.5 bg-white/10 mt-3 w-full">
                <div className={`h-full transition-all ${s.completed ? "bg-green-500" : "bg-[#ff0033]"}`} style={{ width: `${s.percent}%` }} />
              </div>
              <p className="text-neutral-400 text-xs mt-1.5">Fortschritt: {s.done} / {s.total ?? checks.length}</p>
              <button type="button" onClick={() => setDetailFor(s)} data-testid={`platzreife-mdetail-${s.email}`}
                className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-white/15 text-neutral-200 hover:border-[#ff0033] font-mono-lab uppercase tracking-widest text-[10px] px-3 py-2">
                <IdCard size={13} /> Teilnehmer-Details
              </button>
              {s.completed && (
                <div className="mt-3">
                  <CertButton s={s} mobile />
                  {s.certificate?.sent_at && <p className="text-neutral-500 text-[10px] mt-1.5 text-center">Urkunde gesendet am {fmtDateTime(s.certificate.sent_at)}</p>}
                </div>
              )}
            </div>
          ))}
        </div>
        </>
      )}

      {detailFor && (
        <PlatzreifeDetail client={client} student={detailFor} canEditDetails={canEditDetails}
          onClose={() => setDetailFor(null)}
          onSaved={(updated) => {
            setDetailFor(updated);
            setData((d) => ({ ...d, students: d.students.map((x) => x.email === updated.email ? updated : x) }));
          }} />
      )}

      {certFor && (
        <CertificateModal client={client} base={base} student={certFor}
          onClose={() => setCertFor(null)} onSent={(cert) => markSent(certFor.email, cert)} />
      )}
    </div>
  );
};

export default PlatzreifeBoard;
